import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button, Modal, Spin, Empty, Image, Checkbox, Select, Input } from 'antd'
import {
  SearchOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  ThunderboltFilled,
  TrophyFilled,
  FullscreenOutlined,
  CloseOutlined,
  ColumnWidthOutlined,
} from '@ant-design/icons'
import {
  postBuscarSimilaresReemplazantes,
  postRepuestosPorCodigos,
  postFirmarRevision,
  getPlantillaMedidasOpciones,
  getPlantillaMedidas,
} from 'src/services/axiomaService'
import { toast } from 'react-toastify'
import './BuscarReemplazantesSection.scss'

const BATCH_SIZE = 5

// En descripción / jerarquía / marca se prioriza el valor de revisión
// (lo corregido en Axioma) y se cae al dato SAP/catálogo solo si no hay.
const DETALLE_FIELDS = [
  { key: 'repuesto_codigo_material_sap', label: 'Código SAP' },
  {
    key: 'descripcion',
    label: 'Descripción',
    keys: ['revision_descripcion_correcta', 'repuesto_descripcion'],
  },
  { key: 'repuesto_npf', label: 'NPF' },
  {
    key: 'marca',
    label: 'Marca',
    keys: ['revision_marca_nombre', 'marca_nombre'],
  },
  { key: 'repuesto_fabricante', label: 'Fabricante' },
  { key: 'estado', label: 'Estado revisión' },
  { key: 'revision_estado_ax', label: 'Estado AX' },
  {
    key: 'categoria',
    label: 'Categoría',
    keys: ['revision_categoria_nombre', 'categoria_nombre'],
  },
  {
    key: 'subcategoria',
    label: 'Subcategoría',
    keys: ['revision_subcategoria_nombre', 'subcategoria_nombre'],
  },
  {
    key: 'segmento',
    label: 'Segmento',
    keys: ['revision_segmento_nombre', 'segmento_nombre'],
  },
  {
    key: 'subsegmento',
    label: 'Subsegmento',
    keys: ['revision_subsegmento_nombre', 'subsegmento_nombre'],
  },
  { key: 'revision_uso_aplicacion', label: 'Uso / aplicación' },
  { key: 'revision_especificaciones_producto', label: 'Especificaciones' },
  { key: 'revision_especificaciones_electricas', label: 'Espec. eléctricas' },
  { key: 'revision_composicion', label: 'Composición' },
  { key: 'revision_peso_g', label: 'Peso (g)' },
  { key: 'revision_largo_mm', label: 'Largo (mm)' },
  { key: 'revision_ancho_mm', label: 'Ancho (mm)' },
  { key: 'revision_alto_mm', label: 'Alto (mm)' },
  { key: 'revision_diametro_mm', label: 'Diámetro (mm)' },
  { key: 'revision_observacion', label: 'Observación' },
  {
    key: 'revision_descripcion_breve',
    label: 'Descripción breve',
    truncate: 72,
  },
]

const formatSimilitud = (similitud) => {
  if (similitud == null || similitud === '') return '—'
  const valor = Number(similitud)
  if (Number.isNaN(valor)) return '—'
  return `${Math.round(valor * 100)}%`
}

const getFromMapPorCodigo = (map, codigo) => {
  if (!map || !codigo) return null
  if (map.has(codigo)) return map.get(codigo)
  for (const [k, v] of map.entries()) {
    if (normalizarCodigo(k) === codigo) return v
    if (normalizarCodigo(v?.repuesto?.repuesto_codigo_material_sap) === codigo) {
      return v
    }
  }
  return null
}

const cardCoincideCodigo = (card, codigo) => {
  if (!card || !codigo) return false
  return (
    normalizarCodigo(card.codigo) === codigo ||
    normalizarCodigo(card.repuesto?.repuesto_codigo_material_sap) === codigo
  )
}

const normalizarCodigo = (codigo) =>
  String(codigo ?? '')
    .trim()
    .replace(/\.(jpg|jpeg|png|webp)$/i, '')

const parseResultadosPorCodigos = (data) => {
  const map = new Map()
  if (!data) return map

  if (Array.isArray(data.resultados)) {
    data.resultados.forEach((item) => {
      const codigo = normalizarCodigo(
        item.codigo || item.repuestos?.[0]?.repuesto_codigo_material_sap,
      )
      if (!codigo) return
      map.set(codigo, {
        repuesto: item.repuestos?.[0] || null,
        imagenes: Array.isArray(item.imagenes) ? item.imagenes : [],
      })
    })
    return map
  }

  if (Array.isArray(data.repuestos)) {
    const repuesto = data.repuestos[0]
    const codigo = normalizarCodigo(repuesto?.repuesto_codigo_material_sap)
    if (codigo) {
      map.set(codigo, {
        repuesto,
        imagenes: Array.isArray(data.imagenes) ? data.imagenes : [],
      })
    }
  }

  return map
}

const buildCardsForBatch = (candidatosBatch, resultadosMap) =>
  candidatosBatch.map((c) => {
    const key = normalizarCodigo(c.codigo)
    const data = resultadosMap.get(key) || {}
    return {
      key: `${key}-${c.similitud}`,
      similitud: c.similitud,
      codigo: key || c.codigo,
      repuesto: data.repuesto || null,
      imagenes: data.imagenes || [],
    }
  })

const getPrimeraImagenUrl = (imagenes, imagenesFormulario = []) => {
  if (imagenes?.[0]?.signed_url) return imagenes[0].signed_url
  const desdeForm = imagenesFormulario.find((img) => img?.signed_url)
  return desdeForm?.signed_url || null
}

// Todas las URLs firmadas de un material; cae a las imágenes del formulario.
const getImagenesUrls = (imagenes, imagenesFormulario = []) => {
  const urls = (Array.isArray(imagenes) ? imagenes : [])
    .map((img) => img?.signed_url)
    .filter(Boolean)
  if (urls.length) return urls
  return (Array.isArray(imagenesFormulario) ? imagenesFormulario : [])
    .map((img) => img?.signed_url)
    .filter(Boolean)
}

const getValorCampo = (repuesto, field) => {
  if (!repuesto) return null
  const keys = field.keys || [field.key]
  for (const key of keys) {
    const valor = repuesto[key]
    if (valor !== null && valor !== undefined && valor !== '') return valor
  }
  return null
}

const normalizarValorComparacion = (valor) => {
  if (valor === null || valor === undefined) return ''
  return String(valor).trim().replace(/\s+/g, ' ').toUpperCase()
}

const valoresCoinciden = (a, b) => {
  const na = normalizarValorComparacion(a)
  const nb = normalizarValorComparacion(b)
  if (!na || !nb) return false
  if (na === nb) return true
  const fa = parseFloat(na.replace(',', '.'))
  const fb = parseFloat(nb.replace(',', '.'))
  if (!Number.isNaN(fa) && !Number.isNaN(fb) && fa === fb) return true
  return false
}

const formatearValorDisplay = (valor, truncate) => {
  if (valor == null || valor === '') return null
  let texto = String(valor)
  if (truncate && texto.length > truncate) {
    texto = `${texto.slice(0, truncate)}…`
  }
  return texto
}

const buildFilasComparacion = (repuestoIzq, repuestoDer) => {
  const filas = DETALLE_FIELDS.map((field) => {
    const { key, label, truncate } = field
    const leftRaw = getValorCampo(repuestoIzq, field)
    const rightRaw = getValorCampo(repuestoDer, field)
    if (leftRaw == null && rightRaw == null) return null
    return {
      key,
      label,
      left: formatearValorDisplay(leftRaw, truncate),
      right: formatearValorDisplay(rightRaw, truncate),
      leftFull: leftRaw != null ? String(leftRaw) : '',
      rightFull: rightRaw != null ? String(rightRaw) : '',
      match: valoresCoinciden(leftRaw, rightRaw),
    }
  }).filter(Boolean)

  return filas
}

const nombrePorId = (lista, id) => {
  if (id == null || id === '') return null
  const found = (lista || []).find((item) => String(item.id) === String(id))
  return found?.nombre || null
}

const pickIdFormulario = (formId, savedId) =>
  formId !== undefined ? formId : savedId

// Si no hay select, no mostrar el dato AX (la etiqueta gris). Si hay ID,
// el nombre sale del catálogo; si el ID no está en la lista, queda vacío.
const nombresJerarquiaSelect = (id, lista, revisionKey, sapKey) => {
  const nombre = nombrePorId(lista, id)
  return {
    [revisionKey]: nombre,
    [sapKey]: nombre,
  }
}

// revision_*_nombre es el dato AX original (la etiqueta gris del form).
// Lo que ya se guardó —y lo que se ve en los selects— son los IDs sugeridos.
const enriquecerConJerarquiaSugerida = (
  repuesto,
  catalogs = {},
  formValues = {},
) => {
  if (!repuesto) return repuesto

  const categoriaId = pickIdFormulario(
    formValues.categoria,
    repuesto.revision_categoria_sugerida_id,
  )
  const subcategoriaId = pickIdFormulario(
    formValues.subcategoria,
    repuesto.revision_subcategoria_sugerida_id,
  )
  const segmentoId = pickIdFormulario(
    formValues.segmento,
    repuesto.revision_segmento_sugerido_id,
  )
  const subsegmentoId = pickIdFormulario(
    formValues.subsegmento,
    repuesto.revision_subsegmento_sugerido_id,
  )
  const marcaId = pickIdFormulario(
    formValues.marca,
    repuesto.revision_marca_sugerida_id,
  )

  return {
    ...repuesto,
    revision_categoria_sugerida_id:
      categoriaId ?? repuesto.revision_categoria_sugerida_id,
    revision_subcategoria_sugerida_id:
      subcategoriaId ?? repuesto.revision_subcategoria_sugerida_id,
    revision_segmento_sugerido_id:
      segmentoId ?? repuesto.revision_segmento_sugerido_id,
    revision_subsegmento_sugerido_id:
      subsegmentoId ?? repuesto.revision_subsegmento_sugerido_id,
    revision_marca_sugerida_id: marcaId ?? repuesto.revision_marca_sugerida_id,
    ...nombresJerarquiaSelect(
      categoriaId,
      catalogs.categorias,
      'revision_categoria_nombre',
      'categoria_nombre',
    ),
    ...nombresJerarquiaSelect(
      subcategoriaId,
      catalogs.subCategorias,
      'revision_subcategoria_nombre',
      'subcategoria_nombre',
    ),
    ...nombresJerarquiaSelect(
      segmentoId,
      catalogs.segmentos,
      'revision_segmento_nombre',
      'segmento_nombre',
    ),
    ...nombresJerarquiaSelect(
      subsegmentoId,
      catalogs.subSegmentos,
      'revision_subsegmento_nombre',
      'subsegmento_nombre',
    ),
    ...nombresJerarquiaSelect(
      marcaId,
      catalogs.marcas,
      'revision_marca_nombre',
      'marca_nombre',
    ),
  }
}

// Conserva campos de revisión del material del formulario cuando el detalle
// por código no los trae (o los manda vacíos).
const mergeRepuestoOriginal = (detalle, materialForm) => {
  if (!detalle && !materialForm) return null
  if (!detalle) return materialForm
  if (!materialForm) return detalle

  const pickRevision = (campo) =>
    detalle[campo] != null && detalle[campo] !== ''
      ? detalle[campo]
      : materialForm[campo]

  // Los IDs sugeridos viven en la revisión abierta; por_codigos suele traer
  // la jerarquía AX original y no debe pisarlos.
  const pickSugerido = (campo) =>
    materialForm[campo] != null && materialForm[campo] !== ''
      ? materialForm[campo]
      : detalle[campo]

  return {
    ...materialForm,
    ...detalle,
    revision_descripcion_correcta: pickRevision('revision_descripcion_correcta'),
    revision_categoria_sugerida_id: pickSugerido(
      'revision_categoria_sugerida_id',
    ),
    revision_subcategoria_sugerida_id: pickSugerido(
      'revision_subcategoria_sugerida_id',
    ),
    revision_segmento_sugerido_id: pickSugerido('revision_segmento_sugerido_id'),
    revision_subsegmento_sugerido_id: pickSugerido(
      'revision_subsegmento_sugerido_id',
    ),
    revision_marca_sugerida_id: pickSugerido('revision_marca_sugerida_id'),
    revision_estado_ax: pickRevision('revision_estado_ax'),
    revision_descripcion_breve: pickRevision('revision_descripcion_breve'),
    revision_uso_aplicacion: pickRevision('revision_uso_aplicacion'),
    revision_especificaciones_producto: pickRevision(
      'revision_especificaciones_producto',
    ),
    revision_especificaciones_electricas: pickRevision(
      'revision_especificaciones_electricas',
    ),
    revision_composicion: pickRevision('revision_composicion'),
    revision_observacion: pickRevision('revision_observacion'),
    revision_peso_g: pickRevision('revision_peso_g'),
    revision_largo_mm: pickRevision('revision_largo_mm'),
    revision_ancho_mm: pickRevision('revision_ancho_mm'),
    revision_alto_mm: pickRevision('revision_alto_mm'),
    revision_diametro_mm: pickRevision('revision_diametro_mm'),
    revision_json_medidas:
      detalle.revision_json_medidas ??
      detalle.json_medidas ??
      materialForm.revision_json_medidas ??
      materialForm.json_medidas ??
      null,
  }
}

// ==================== Plantilla de medidas (solo lectura / informativa) ====================
const getPlantillaDeRepuesto = (repuesto) =>
  repuesto?.revision_json_medidas ?? repuesto?.json_medidas ?? null

const extraerMedidasPlantilla = (jsonMedidas) => {
  if (!jsonMedidas) return []
  const lista = Array.isArray(jsonMedidas)
    ? jsonMedidas
    : Array.isArray(jsonMedidas.especificaciones)
    ? jsonMedidas.especificaciones
    : []
  return lista.map((m) => ({
    medida: m.medida ?? '',
    label: m.definicion_cotas ?? m.descripcion ?? m.nombre ?? '',
    valor: m.valor,
    tipoCampo: m.tipo_campo ?? 'Num',
  }))
}

const medidaTieneValor = (valor) =>
  valor !== null &&
  valor !== undefined &&
  valor !== '' &&
  valor !== 0 &&
  valor !== '0'

// IDs de categoría/subcategoría del repuesto para consultar las plantillas
// disponibles (las mismas que aparecen en "Ver medidas"). Se prioriza la
// categoría sugerida en la revisión y se cae a la categoría AX del repuesto.
const getCategoriaIdsDeRepuesto = (repuesto) => ({
  categoriaId:
    repuesto?.revision_categoria_sugerida_id ??
    repuesto?.repuesto_categoria_id ??
    repuesto?.categoria_id ??
    null,
  subcategoriaId:
    repuesto?.revision_subcategoria_sugerida_id ??
    repuesto?.repuesto_subcategoria_id ??
    repuesto?.subcategoria_id ??
    null,
})

// Carga las imágenes de referencia de una plantilla, re-firmando SIEMPRE desde
// gs_url: los signed_url guardados suelen estar vencidos (por eso no cargaban).
const useImagenesPlantilla = (open, jsonMedidas) => {
  const [imagenes, setImagenes] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !jsonMedidas || Array.isArray(jsonMedidas)) {
      setImagenes([])
      return undefined
    }
    let cancelado = false
    const cargarImagenes = async () => {
      const procesadas = Array.isArray(jsonMedidas.imagenes_procesadas)
        ? jsonMedidas.imagenes_procesadas
        : []
      const gsUrls = [
        ...new Set(
          [
            ...procesadas.map((p) => p?.gs_url).filter(Boolean),
            jsonMedidas.gs_url,
          ].filter(Boolean),
        ),
      ]
      const signedGuardados = procesadas
        .map((p) => p?.signed_url)
        .filter(Boolean)

      if (!gsUrls.length) {
        setImagenes(signedGuardados)
        return
      }

      setLoading(true)
      try {
        const resultados = await Promise.all(
          gsUrls.map((url) => postFirmarRevision(url)),
        )
        const urls = resultados.map((r) => r?.url).filter(Boolean)
        if (!cancelado) {
          setImagenes(urls.length ? urls : signedGuardados)
        }
      } finally {
        if (!cancelado) setLoading(false)
      }
    }
    cargarImagenes()
    return () => {
      cancelado = true
    }
  }, [open, jsonMedidas])

  return { imagenes, loading }
}

// Una columna de plantilla (medidas + imagen de referencia) para un material.
// Muestra la plantilla asignada y permite consultar las demás plantillas
// disponibles para la categoría/subcategoría (las de "Ver medidas").
const PlantillaColumna = ({ open, repuesto, codigo, rol }) => {
  const jsonAsignado = useMemo(
    () => getPlantillaDeRepuesto(repuesto),
    [repuesto],
  )
  const descripcionAsignada =
    jsonAsignado && !Array.isArray(jsonAsignado)
      ? jsonAsignado.definicion_cotas ?? null
      : null

  const { categoriaId, subcategoriaId } = useMemo(
    () => getCategoriaIdsDeRepuesto(repuesto),
    [repuesto],
  )

  const [opciones, setOpciones] = useState([])
  const [seleccion, setSeleccion] = useState(null)
  const [jsonConsultado, setJsonConsultado] = useState(null)
  const [loadingPlantilla, setLoadingPlantilla] = useState(false)

  // Cargar las plantillas disponibles para esta categoría/subcategoría.
  useEffect(() => {
    if (!open || !categoriaId || !subcategoriaId) {
      setOpciones([])
      return undefined
    }
    let cancelado = false
    const cargarOpciones = async () => {
      const ops = await getPlantillaMedidasOpciones(categoriaId, subcategoriaId)
      if (!cancelado) setOpciones(Array.isArray(ops) ? ops : [])
    }
    cargarOpciones()
    return () => {
      cancelado = true
    }
  }, [open, categoriaId, subcategoriaId])

  // Al abrir (o cambiar de material) seleccionar la plantilla asignada.
  useEffect(() => {
    if (!open) return
    setSeleccion(descripcionAsignada)
    setJsonConsultado(null)
  }, [open, descripcionAsignada])

  const esSeleccionAsignada =
    seleccion != null && seleccion === descripcionAsignada
  // Plantilla a mostrar: la asignada por defecto o la consultada al elegir otra.
  const jsonMostrado =
    seleccion == null || esSeleccionAsignada ? jsonAsignado : jsonConsultado

  const medidas = useMemo(
    () => extraerMedidasPlantilla(jsonMostrado),
    [jsonMostrado],
  )
  const { imagenes, loading: loadingImagenes } = useImagenesPlantilla(
    open,
    jsonMostrado,
  )

  const handleSeleccion = async (descripcion) => {
    setSeleccion(descripcion)
    if (descripcion === descripcionAsignada) {
      setJsonConsultado(null)
      return
    }
    if (!categoriaId || !subcategoriaId) return
    setLoadingPlantilla(true)
    try {
      const resp = await getPlantillaMedidas(
        categoriaId,
        subcategoriaId,
        descripcion,
      )
      const data = resp?.data ?? resp
      setJsonConsultado(data && !Array.isArray(data) ? data : null)
    } finally {
      setLoadingPlantilla(false)
    }
  }

  // Opciones del selector: las disponibles + la asignada si no estuviera.
  const opcionesSelect = useMemo(() => {
    const base = opciones.map((o) => o.descripcion).filter(Boolean)
    if (descripcionAsignada && !base.includes(descripcionAsignada)) {
      return [descripcionAsignada, ...base]
    }
    return base
  }, [opciones, descripcionAsignada])

  const hayOpciones = opcionesSelect.length > 0
  const hayImagenes = loadingImagenes || imagenes.length > 0
  const hayMedidas = medidas.length > 0

  return (
    <div
      className="plantilla-info__col"
      style={{ flex: '1 1 320px', minWidth: 280 }}
    >
      <div className="plantilla-info__col-head">
        <span className="plantilla-info__rol">{rol}</span>
        {codigo && <span className="plantilla-info__codigo">{codigo}</span>}
      </div>

      {hayOpciones && (
        <div style={{ marginBottom: 12 }}>
          <Select
            placeholder="Seleccione una plantilla"
            style={{ width: '100%' }}
            value={seleccion ?? undefined}
            onChange={handleSeleccion}
            loading={loadingPlantilla}
            options={opcionesSelect.map((d) => ({
              value: d,
              label: d === descripcionAsignada ? `${d} (asignada)` : d,
            }))}
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        </div>
      )}

      {loadingPlantilla ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : !hayMedidas ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            hayOpciones
              ? 'Seleccione una plantilla para ver las medidas'
              : 'Este material no tiene plantilla asignada'
          }
        />
      ) : (
        <>
          {hayImagenes && (
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              {loadingImagenes ? (
                <Spin />
              ) : imagenes.length > 1 ? (
                <Image.PreviewGroup>
                  {imagenes.map((url) => (
                    <Image
                      key={url}
                      src={url}
                      style={{
                        maxWidth: '100%',
                        marginBottom: 8,
                        borderRadius: 8,
                      }}
                    />
                  ))}
                </Image.PreviewGroup>
              ) : (
                <Image
                  src={imagenes[0]}
                  style={{ maxWidth: '100%', borderRadius: 8 }}
                />
              )}
            </div>
          )}
          <div style={{ paddingRight: 6 }}>
            {medidas.map((m) => {
              const esNumerico = m.tipoCampo !== 'Text' && m.tipoCampo !== 'ARR'
              const tieneVal = medidaTieneValor(m.valor)
              return (
                <div
                  key={`${m.medida}-${m.label}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 12px',
                    marginBottom: 8,
                    border: '1px solid #dce8f3',
                    borderRadius: 8,
                    background: '#f0f5fa',
                  }}
                >
                  <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.78)' }}>
                    {m.medida ? (
                      <b style={{ color: '#1b3a5f' }}>{m.medida}: </b>
                    ) : null}
                    {m.label}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      color: tieneVal ? '#0f2944' : '#94a3b8',
                    }}
                  >
                    {tieneVal ? m.valor : '—'}
                    {esNumerico && tieneVal ? ' mm' : ''}
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// Muestra ambas plantillas (original y candidato) en paralelo.
const PlantillaInfoModal = ({ open, onClose, original, candidato }) => (
  <Modal
    title="Plantillas de medidas"
    open={open}
    onCancel={onClose}
    footer={[
      <Button key="cerrar" onClick={onClose}>
        Cerrar
      </Button>,
    ]}
    width={940}
    centered
    destroyOnClose
    zIndex={1100}
    styles={{
      body: {
        maxHeight: '72vh',
        overflowY: 'auto',
      },
    }}
  >
    <div
      className="plantilla-info"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 20,
        alignItems: 'flex-start',
      }}
    >
      <PlantillaColumna
        open={open}
        repuesto={original?.repuesto}
        codigo={original?.codigo}
        rol="Original"
      />
      <PlantillaColumna
        open={open}
        repuesto={candidato?.repuesto}
        codigo={candidato?.codigo}
        rol="Candidato"
      />
    </div>
  </Modal>
)

const VsProfileCard = ({ side, codigo, imagenUrl, rol }) => (
  <div className={`vs-compare__profile-card vs-compare__profile-card--${side}`}>
    <div className="vs-compare__profile-image-wrap">
      {imagenUrl ? (
        <Image
          src={imagenUrl}
          alt={codigo}
          className="vs-compare__profile-img"
          preview={{ mask: 'Zoom' }}
        />
      ) : (
        <div className="vs-compare__profile-placeholder">Sin foto</div>
      )}
    </div>
    <div className="vs-compare__profile-footer">
      <span className="vs-compare__profile-rol">{rol}</span>
      <span className="vs-compare__profile-code">{codigo}</span>
    </div>
  </div>
)

// Un lado del visor "Ver imágenes en paralelo": imagen grande + tira de
// miniaturas para seleccionar entre todas las imágenes del material.
const VsFullscreenSide = ({ rol, codigo, imagenes, index, onSelect }) => {
  const actual = imagenes[index] ?? imagenes[0] ?? null
  return (
    <figure className="vs-fullscreen__item">
      {actual ? (
        <img src={actual} alt={codigo} />
      ) : (
        <div className="vs-fullscreen__empty">Sin foto</div>
      )}
      {imagenes.length > 1 && (
        <div className="vs-fullscreen__thumbs">
          {imagenes.map((url, i) => (
            <button
              type="button"
              key={url}
              className={`vs-fullscreen__thumb${
                i === index ? ' vs-fullscreen__thumb--active' : ''
              }`}
              onClick={() => onSelect(i)}
              aria-label={`Imagen ${i + 1} de ${imagenes.length}`}
            >
              <img src={url} alt={`${codigo} ${i + 1}`} />
            </button>
          ))}
        </div>
      )}
      <figcaption className="vs-fullscreen__caption">
        <span>{rol}</span>
        {codigo}
        {imagenes.length > 1
          ? ` · ${Math.min(index, imagenes.length - 1) + 1}/${imagenes.length}`
          : ''}
      </figcaption>
    </figure>
  )
}

// Exportada para reutilizarse en la pantalla de Reemplazantes (misma
// comparación que el modal del formulario, solo cambia quién la abre).
export const TinderVsComparison = ({
  materialOriginal,
  comparacionActiva,
  imagenesMaterial,
  codigoOriginal,
  yaEsReemplazante,
  yaEsSustituto,
  esMismoMaterial,
  puedeMatchReemplazante,
  puedeMatchSustituto,
  puedeQuitarReemplazante,
  puedeQuitarSustituto,
  accionEnCurso,
  onVolverCandidatos,
  onMatchReemplazante,
  onQuitarReemplazante,
  onMatchSustituto,
  onQuitarSustituto,
}) => {
  const repuestoIzq = materialOriginal.repuesto
  const repuestoDer = comparacionActiva.repuesto
  const filas = useMemo(
    () => buildFilasComparacion(repuestoIzq, repuestoDer),
    [repuestoIzq, repuestoDer],
  )
  const totalMatches = filas.filter((f) => f.match).length
  const esMatchManual =
    Boolean(comparacionActiva.busquedaManual) ||
    comparacionActiva.similitud == null

  const imagenesIzq = useMemo(
    () => getImagenesUrls(materialOriginal.imagenes, imagenesMaterial),
    [materialOriginal.imagenes, imagenesMaterial],
  )
  const imagenesDer = useMemo(
    () => getImagenesUrls(comparacionActiva.imagenes),
    [comparacionActiva.imagenes],
  )
  // El preview de las tarjetas y el match siempre usan la primera imagen.
  const imagenIzq = imagenesIzq[0] || null
  const imagenDer = imagenesDer[0] || null

  const codigoDer =
    repuestoDer?.repuesto_codigo_material_sap || comparacionActiva.codigo

  const [verFull, setVerFull] = useState(false)
  const [idxIzq, setIdxIzq] = useState(0)
  const [idxDer, setIdxDer] = useState(0)
  const [plantillasOpen, setPlantillasOpen] = useState(false)
  const hayAlgunaImagen = imagenesIzq.length > 0 || imagenesDer.length > 0

  useEffect(() => {
    if (!verFull) return undefined
    // Reiniciar a la primera imagen cada vez que se abre el visor.
    setIdxIzq(0)
    setIdxDer(0)
    const onKey = (e) => {
      if (e.key === 'Escape') setVerFull(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [verFull])

  return (
    <div className="vs-compare">
      <header className="vs-compare__toolbar">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onVolverCandidatos}
          className="vs-compare__back"
        >
          Candidatos
        </Button>
        {(yaEsReemplazante || yaEsSustituto) && (
          <span className="vs-compare__status">
            <CheckCircleFilled />
            {yaEsReemplazante && yaEsSustituto
              ? 'Reemplazante + Sustituto'
              : yaEsReemplazante
              ? 'Reemplazante'
              : 'Sustituto'}
          </span>
        )}
        <div className="vs-compare__toolbar-actions">
          {yaEsReemplazante ? (
            <Button
              danger
              type="primary"
              className="vs-compare__btn-unmatch"
              onClick={onQuitarReemplazante}
              loading={accionEnCurso === 'unmatch-reempl'}
              disabled={!puedeQuitarReemplazante || accionEnCurso != null}
            >
              Eliminar de reemplazantes
            </Button>
          ) : (
            <Button
              type="primary"
              className="vs-compare__btn-match"
              onClick={onMatchReemplazante}
              loading={accionEnCurso === 'match-reempl'}
              disabled={
                !puedeMatchReemplazante ||
                accionEnCurso != null ||
                esMismoMaterial
              }
              icon={<ThunderboltFilled />}
            >
              {esMismoMaterial ? 'Mismo material' : 'Reemplazante'}
            </Button>
          )}
          {yaEsSustituto ? (
            <Button
              danger
              type="primary"
              className="vs-compare__btn-unmatch"
              onClick={onQuitarSustituto}
              loading={accionEnCurso === 'unmatch-sust'}
              disabled={!puedeQuitarSustituto || accionEnCurso != null}
            >
              Eliminar de sustitutos
            </Button>
          ) : (
            <Button
              type="primary"
              className="vs-compare__btn-sust"
              onClick={onMatchSustituto}
              loading={accionEnCurso === 'match-sust'}
              disabled={
                !puedeMatchSustituto ||
                accionEnCurso != null ||
                esMismoMaterial
              }
              icon={<ThunderboltFilled />}
            >
              {esMismoMaterial ? 'Mismo material' : 'Sustituto'}
            </Button>
          )}
        </div>
      </header>

      <div className="vs-compare__summary">
        <div className="vs-compare__cards">
          <VsProfileCard
            side="left"
            codigo={codigoOriginal}
            imagenUrl={imagenIzq}
            rol="Original"
          />
          <div
            className={`vs-compare__flame${
              esMatchManual ? ' vs-compare__flame--manual' : ''
            }`}
            aria-label={esMatchManual ? 'Match manual' : 'Similitud'}
          >
            {esMatchManual ? (
              <>
                <span className="vs-compare__flame-value">Match</span>
                <span className="vs-compare__flame-label">manual</span>
              </>
            ) : (
              <>
                <span className="vs-compare__flame-value">
                  {formatSimilitud(comparacionActiva.similitud)}
                </span>
                <span className="vs-compare__flame-label">Match IA</span>
              </>
            )}
          </div>
          <VsProfileCard
            side="right"
            codigo={codigoDer}
            imagenUrl={imagenDer}
            rol="Candidato"
          />
        </div>
        <p className="vs-compare__summary-stats">
          <strong className="vs-compare__summary-stats--green">
            {totalMatches}
          </strong>{' '}
          coinciden · {filas.length} atributos
        </p>
        <div className="vs-compare__summary-actions">
          <Button
            size="small"
            icon={<ColumnWidthOutlined />}
            onClick={() => setPlantillasOpen(true)}
            className="vs-compare__plantilla-btn"
          >
            Ver plantillas
          </Button>
          <Button
            size="small"
            icon={<FullscreenOutlined />}
            onClick={() => setVerFull(true)}
            disabled={!hayAlgunaImagen}
            className="vs-compare__fullscreen-btn"
          >
            Ver imágenes en paralelo
          </Button>
        </div>
      </div>

      {verFull &&
        createPortal(
          <div
            className="vs-fullscreen"
            role="dialog"
            aria-modal="true"
            aria-label="Imágenes en paralelo"
          >
            <button
              type="button"
              className="vs-fullscreen__backdrop"
              aria-label="Cerrar"
              onClick={() => setVerFull(false)}
            />
            <button
              type="button"
              className="vs-fullscreen__close"
              onClick={() => setVerFull(false)}
              aria-label="Cerrar"
            >
              <CloseOutlined />
            </button>
            <div className="vs-fullscreen__grid">
              <VsFullscreenSide
                rol="Original"
                codigo={codigoOriginal}
                imagenes={imagenesIzq}
                index={idxIzq}
                onSelect={setIdxIzq}
              />
              <VsFullscreenSide
                rol="Candidato"
                codigo={codigoDer}
                imagenes={imagenesDer}
                index={idxDer}
                onSelect={setIdxDer}
              />
            </div>
          </div>,
          document.body,
        )}

      <div className="vs-compare__table-head">
        <span>Material original</span>
        <span>Atributo</span>
        <span>Candidato</span>
      </div>

      <div className="vs-compare__grid">
        {filas.map((fila) => (
          <div
            key={fila.key}
            className={`vs-compare__row${
              fila.match ? ' vs-compare__row--match' : ''
            }`}
          >
            <div
              className={`vs-compare__val vs-compare__val--left${
                !fila.left ? ' vs-compare__val--empty' : ''
              }`}
              title={fila.leftFull || undefined}
            >
              {fila.left || '—'}
            </div>
            <div className="vs-compare__label">
              {fila.match && (
                <CheckCircleFilled className="vs-compare__check" />
              )}
              {fila.label}
            </div>
            <div
              className={`vs-compare__val vs-compare__val--right${
                !fila.right ? ' vs-compare__val--empty' : ''
              }`}
              title={fila.rightFull || undefined}
            >
              {fila.right || '—'}
            </div>
          </div>
        ))}
      </div>

      <PlantillaInfoModal
        open={plantillasOpen}
        onClose={() => setPlantillasOpen(false)}
        original={{ repuesto: repuestoIzq, codigo: codigoOriginal }}
        candidato={{ repuesto: repuestoDer, codigo: codigoDer }}
      />
    </div>
  )
}

const CONFETTI_COLORS = [
  '#235a97',
  '#2c6196',
  '#5b89b8',
  '#22c55e',
  '#f59e0b',
  '#38bdf8',
]

export const MatchCelebration = ({
  imagenIzq,
  imagenDer,
  codigoIzq,
  codigoDer,
  similitud,
  tipo,
}) => {
  const confetti = useMemo(
    () =>
      Array.from({ length: 32 }, (_, i) => ({
        id: `confetti-${i}`,
        left: `${(i * 33 + 7) % 100}%`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${(i % 12) * 0.06}s`,
        duration: `${1.1 + ((i * 7) % 9) / 10}s`,
        rotate: `${(i * 47) % 360}deg`,
      })),
    [],
  )
  const hearts = useMemo(
    () =>
      Array.from({ length: 9 }, (_, i) => ({
        id: `heart-${i}`,
        left: `${10 + ((i * 23 + 5) % 80)}%`,
        delay: `${0.1 + (i % 9) * 0.12}s`,
        size: `${16 + ((i * 5) % 18)}px`,
      })),
    [],
  )

  return (
    <div className="match-celebration" role="alert" aria-label="¡Match!">
      <div className="match-celebration__backdrop" />

      <div className="match-celebration__confetti" aria-hidden>
        {confetti.map((c) => (
          <span
            key={c.id}
            className="mc-confetti"
            style={{
              left: c.left,
              background: c.color,
              animationDelay: c.delay,
              animationDuration: c.duration,
              transform: `rotate(${c.rotate})`,
            }}
          />
        ))}
      </div>

      {hearts.map((h) => (
        <span
          key={h.id}
          className="mc-heart"
          aria-hidden
          style={{
            left: h.left,
            animationDelay: h.delay,
            fontSize: h.size,
          }}
        >
          <ThunderboltFilled />
        </span>
      ))}

      <div className="match-celebration__content">
        <div className="match-celebration__cards">
          <div className="mc-card mc-card--left">
            {imagenIzq ? (
              <img src={imagenIzq} alt={codigoIzq} />
            ) : (
              <span className="mc-card__noimg">Sin foto</span>
            )}
            <span className="mc-card__code">{codigoIzq}</span>
          </div>

          <div className="mc-heartburst">
            <ThunderboltFilled />
          </div>

          <div className="mc-card mc-card--right">
            {imagenDer ? (
              <img src={imagenDer} alt={codigoDer} />
            ) : (
              <span className="mc-card__noimg">Sin foto</span>
            )}
            <span className="mc-card__code">{codigoDer}</span>
          </div>
        </div>

        <div className="match-celebration__title">¡MATCH!</div>
        <div className="match-celebration__subtitle">
          <ThunderboltFilled /> {tipo ? `${tipo} vinculado` : 'Vinculado'}
          {similitud ? ` · ${similitud} afinidad` : ''}
        </div>
      </div>
    </div>
  )
}

const esRepuestoEnReemplazantes = (repuestoId, reemplazantes) =>
  repuestoId != null &&
  reemplazantes.some((r) => r.repuesto_id === repuestoId)

const getSimilitudNivel = (similitud) => {
  const valor = Number(similitud)
  if (Number.isNaN(valor)) return 'low'
  if (valor >= 0.85) return 'high'
  if (valor >= 0.6) return 'mid'
  return 'low'
}

const CandidatoReemplazanteCard = ({
  card,
  onClick,
  reemplazantes,
  sustitutos = [],
  index = 0,
}) => {
  const { repuesto, codigo, similitud, busquedaManual } = card
  const codigoMostrar =
    repuesto?.repuesto_codigo_material_sap || codigo
  const imagenMini = card.imagenes?.[0]?.signed_url
  const esReemplazante = esRepuestoEnReemplazantes(
    repuesto?.repuesto_id,
    reemplazantes,
  )
  const esSustituto = esRepuestoEnReemplazantes(
    repuesto?.repuesto_id,
    sustitutos,
  )
  const tieneRelacion = esReemplazante || esSustituto
  const esManual = Boolean(busquedaManual) || similitud == null
  const nivel = esManual ? 'manual' : getSimilitudNivel(similitud)

  return (
    <button
      type="button"
      className={`candidato-reemplazante-card candidato-reemplazante-card--clickable candidato-reemplazante-card--${nivel}${
        tieneRelacion ? ' candidato-reemplazante-card--matched' : ''
      }`}
      style={{ '--card-index': index % 5 }}
      onClick={() => onClick(card)}
    >
      <span className="candidato-reemplazante-shine" aria-hidden />
      {index < 3 && !tieneRelacion && !esManual && (
        <span
          className={`candidato-reemplazante-rank candidato-reemplazante-rank--${index}`}
          title={`Top ${index + 1} en afinidad`}
        >
          <TrophyFilled />#{index + 1}
        </span>
      )}
      {tieneRelacion && (
        <span className="candidato-reemplazante-rel-badges">
          {esReemplazante && (
            <span
              className="candidato-reemplazante-rel-badge candidato-reemplazante-rel-badge--reempl"
              title="Reemplazante"
            >
              <CheckCircleFilled />
              Reemplazante
            </span>
          )}
          {esSustituto && (
            <span
              className="candidato-reemplazante-rel-badge candidato-reemplazante-rel-badge--sust"
              title="Sustituto"
            >
              <CheckCircleFilled />
              Sustituto
            </span>
          )}
        </span>
      )}
      <div className="candidato-reemplazante-card-layout">
        <div className="candidato-reemplazante-thumb-wrap">
          {imagenMini ? (
            <img src={imagenMini} alt="" className="candidato-reemplazante-thumb" />
          ) : (
            <span className="candidato-reemplazante-thumb-empty">Sin foto</span>
          )}
          <span
            className={`candidato-reemplazante-affinity candidato-reemplazante-affinity--${nivel}`}
          >
            {esManual ? 'Manual' : formatSimilitud(similitud)}
          </span>
        </div>
        <div className="candidato-reemplazante-card-body">
          <div className="candidato-reemplazante-card-header">
            <span className="candidato-reemplazante-codigo">{codigoMostrar}</span>
          </div>
          {(repuesto?.revision_descripcion_correcta ||
            repuesto?.repuesto_descripcion) && (
            <p className="candidato-reemplazante-desc">
              {repuesto.revision_descripcion_correcta ||
                repuesto.repuesto_descripcion}
            </p>
          )}
          <p className="candidato-reemplazante-meta">
            {[
              repuesto?.revision_marca_nombre || repuesto?.marca_nombre,
              repuesto?.revision_categoria_nombre || repuesto?.categoria_nombre,
            ]
              .filter(Boolean)
              .join(' · ') || 'Ver comparación'}
            <span className="candidato-reemplazante-cta">Comparar →</span>
          </p>
        </div>
      </div>
    </button>
  )
}

/**
 * Búsqueda de candidatos reemplazantes por similitud (HU).
 * Modal con cards; al hacer clic, vista VS material original vs candidato.
 */
const BuscarReemplazantesSection = ({
  codigoMaterial,
  material,
  imagenesMaterial = [],
  reemplazantes = [],
  sustitutos = [],
  isReadOnly = false,
  onMatchReemplazante,
  onUnmatchReemplazante,
  onMatchSustituto,
  onUnmatchSustituto,
  disabled = false,
  cooldownSeconds = 0,
  categorias = [],
  subCategorias = [],
  segmentos = [],
  subSegmentos = [],
  marcas = [],
  form,
  // Permite usar la sección solo como modal (sin el botón "Buscar similares"),
  // p. ej. al hacer clic en un material en la pantalla de Reemplazantes.
  mostrarBoton = true,
  // Dispara la búsqueda al montar (mismo flujo del botón). Para re-abrir,
  // el consumidor remonta el componente cambiando su `key`.
  autoBuscar = false,
}) => {
  const [loadingBuscar, setLoadingBuscar] = useState(false)
  const [mismaMarca, setMismaMarca] = useState(false)
  const [mismaCategoria, setMismaCategoria] = useState(false)
  const [mismaSubcategoria, setMismaSubcategoria] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [candidatos, setCandidatos] = useState([])
  const [cards, setCards] = useState([])
  const [loadedCount, setLoadedCount] = useState(0)
  const [loadingDetalle, setLoadingDetalle] = useState(false)
  const [materialOriginal, setMaterialOriginal] = useState({
    repuesto: null,
    imagenes: [],
  })
  const [comparacionActiva, setComparacionActiva] = useState(null)
  const [accionEnCurso, setAccionEnCurso] = useState(null)
  const [celebracion, setCelebracion] = useState(null)
  const [codigoBusqueda, setCodigoBusqueda] = useState('')
  const [loadingCodigo, setLoadingCodigo] = useState(false)

  const catalogs = useMemo(
    () => ({ categorias, subCategorias, segmentos, subSegmentos, marcas }),
    [categorias, subCategorias, segmentos, subSegmentos, marcas],
  )

  const getJerarquiaFormulario = useCallback(() => {
    const values = form?.getFieldsValue?.() || {}
    return {
      categoria: values.categoria,
      subcategoria: values.subcategoria,
      segmento: values.segmento,
      subsegmento: values.subsegmento,
      marca: values.marca,
    }
  }, [form])

  const cargarDetalleCodigos = useCallback(async (codigos) => {
    if (!codigos.length) return new Map()
    const response = await postRepuestosPorCodigos(codigos)
    return parseResultadosPorCodigos(response)
  }, [])

  const cargarLote = useCallback(
    async (listaCandidatos, desde) => {
      const lote = listaCandidatos.slice(desde, desde + BATCH_SIZE)
      if (!lote.length) return []

      const codigos = lote.map((c) => normalizarCodigo(c.codigo)).filter(Boolean)
      const resultadosMap = await cargarDetalleCodigos(codigos)
      return buildCardsForBatch(lote, resultadosMap)
    },
    [cargarDetalleCodigos],
  )

  // Carga el detalle de todos los candidatos que falten. Necesario para que los
  // filtros por categoría/subcategoría apliquen sobre la lista completa y no
  // solo sobre el lote visible.
  const cargarRestantes = useCallback(async () => {
    setLoadingDetalle(true)
    try {
      const restantes = candidatos.slice(loadedCount)
      // Cargar en grupos en paralelo para no enviar una sola petición enorme.
      const CHUNK = 25
      const grupos = []
      for (let i = 0; i < restantes.length; i += CHUNK) {
        grupos.push(restantes.slice(i, i + CHUNK))
      }
      const mapas = await Promise.all(
        grupos.map((grupo) =>
          cargarDetalleCodigos(
            grupo.map((c) => normalizarCodigo(c.codigo)).filter(Boolean),
          ),
        ),
      )
      const resultadosMap = new Map()
      mapas.forEach((m) => m.forEach((v, k) => resultadosMap.set(k, v)))
      const nuevas = buildCardsForBatch(restantes, resultadosMap)
      setCards((prev) => {
        const vistos = new Set(prev.map((c) => normalizarCodigo(c.codigo)))
        return [
          ...prev,
          ...nuevas.filter((c) => {
            const k = normalizarCodigo(c.codigo)
            if (!k || vistos.has(k)) return false
            vistos.add(k)
            return true
          }),
        ]
      })
      setLoadedCount(candidatos.length)
    } catch {
      toast.error('Error al cargar los candidatos para filtrar')
    } finally {
      setLoadingDetalle(false)
    }
  }, [candidatos, loadedCount, cargarDetalleCodigos])

  const cargarMaterialOriginal = useCallback(async () => {
    const codigo = normalizarCodigo(codigoMaterial)
    if (!codigo) {
      const data = {
        repuesto: enriquecerConJerarquiaSugerida(
          material || null,
          catalogs,
          getJerarquiaFormulario(),
        ),
        imagenes: [],
      }
      setMaterialOriginal(data)
      return data
    }

    const map = await cargarDetalleCodigos([codigo])
    const data = getFromMapPorCodigo(map, codigo)
    // Mezcla detalle API + material del formulario para no perder
    // descripción correcta / IDs sugeridos de la revisión abierta.
    const repuestoConPlantilla = enriquecerConJerarquiaSugerida(
      mergeRepuestoOriginal(data?.repuesto || null, material || null),
      catalogs,
      getJerarquiaFormulario(),
    )
    const resultado = {
      repuesto: repuestoConPlantilla,
      imagenes: data?.imagenes?.length
        ? data.imagenes
        : imagenesMaterial.filter((img) => img?.signed_url),
    }
    setMaterialOriginal(resultado)
    return resultado
  }, [
    codigoMaterial,
    material,
    imagenesMaterial,
    cargarDetalleCodigos,
    catalogs,
    getJerarquiaFormulario,
  ])

  const resetModalState = () => {
    setCandidatos([])
    setCards([])
    setLoadedCount(0)
    setLoadingDetalle(false)
    setComparacionActiva(null)
    setMaterialOriginal({ repuesto: null, imagenes: [] })
    setCelebracion(null)
    setMismaCategoria(false)
    setMismaSubcategoria(false)
    setCodigoBusqueda('')
    setLoadingCodigo(false)
  }

  const handleCerrarModal = () => {
    setModalOpen(false)
    resetModalState()
  }

  // Núcleo de la búsqueda; reutilizable para la búsqueda inicial y para
  // re-buscar cuando se cambia el filtro "misma marca" dentro del modal.
  const buscarCandidatos = async (mismaMarcaValue) => {
    setLoadingDetalle(true)
    setCards([])
    setLoadedCount(0)
    setComparacionActiva(null)
    try {
      const response = await postBuscarSimilaresReemplazantes(
        normalizarCodigo(codigoMaterial),
        0.8,
        mismaMarcaValue,
      )
      const lista = Array.isArray(response?.candidatos)
        ? response.candidatos
        : []
      setCandidatos(lista)

      if (!lista.length) return lista

      await cargarMaterialOriginal()
      const primerLote = await cargarLote(lista, 0)
      setCards(primerLote)
      setLoadedCount(Math.min(BATCH_SIZE, lista.length))
      return lista
    } finally {
      setLoadingDetalle(false)
    }
  }

  const handleBuscar = async () => {
    if (!codigoMaterial || disabled) return

    // La primera búsqueda siempre va sin filtro de marca; el usuario lo
    // activa/desactiva dentro del modal.
    setLoadingBuscar(true)
    setMismaMarca(false)
    setMismaCategoria(false)
    setMismaSubcategoria(false)
    setModalOpen(true)
    try {
      const lista = await buscarCandidatos(false)
      if (!lista.length) {
        toast.info('No se encontraron candidatos reemplazantes')
      }
    } catch {
      toast.error('Error al buscar reemplazantes')
    } finally {
      setLoadingBuscar(false)
    }
  }

  // Apertura automática: reproduce el clic en "Buscar similares" al montar.
  useEffect(() => {
    if (autoBuscar) handleBuscar()
    // solo al montar; se re-abre remontando el componente con otra `key`
  }, [])

  const handleToggleMismaMarca = async (checked) => {
    if (loadingDetalle) return
    setMismaMarca(checked)
    try {
      const lista = await buscarCandidatos(checked)
      if (!lista.length) {
        toast.info('No se encontraron candidatos con este filtro')
      }
    } catch {
      toast.error('Error al actualizar la búsqueda')
    }
  }

  const handleVerMas = async () => {
    if (loadedCount >= candidatos.length || loadingDetalle) return

    setLoadingDetalle(true)
    try {
      const siguienteLote = await cargarLote(candidatos, loadedCount)
      setCards((prev) => {
        const vistos = new Set(prev.map((c) => normalizarCodigo(c.codigo)))
        return [
          ...prev,
          ...siguienteLote.filter((c) => {
            const k = normalizarCodigo(c.codigo)
            if (!k || vistos.has(k)) return false
            vistos.add(k)
            return true
          }),
        ]
      })
      setLoadedCount((prev) =>
        Math.min(prev + BATCH_SIZE, candidatos.length),
      )
    } catch {
      toast.error('Error al cargar más candidatos')
    } finally {
      setLoadingDetalle(false)
    }
  }

  const handleCardClick = (card) => {
    setComparacionActiva(card)
  }

  const handleBuscarPorCodigo = async (valor) => {
    const codigo = normalizarCodigo(valor || codigoBusqueda).trim()
    if (!codigo) {
      toast.info('Ingrese un código SAP')
      return
    }
    if (codigo === normalizarCodigo(codigoMaterial)) {
      toast.info('Ese es el material que estás revisando')
      return
    }

    setLoadingCodigo(true)
    try {
      if (!materialOriginal.repuesto) {
        await cargarMaterialOriginal()
      }

      const existente = cards.find((c) => cardCoincideCodigo(c, codigo))
      if (existente) {
        setComparacionActiva(existente)
        return
      }

      const map = await cargarDetalleCodigos([codigo])
      const data = getFromMapPorCodigo(map, codigo)
      if (!data?.repuesto) {
        toast.info('No se encontró un material con ese código')
        return
      }

      const candidatoHu = candidatos.find(
        (c) => normalizarCodigo(c.codigo) === codigo,
      )
      const card = {
        key: `manual-${codigo}`,
        similitud: candidatoHu?.similitud ?? null,
        codigo,
        repuesto: data.repuesto,
        imagenes: data.imagenes || [],
        busquedaManual: !candidatoHu,
      }
      setCards((prev) => [
        card,
        ...prev.filter((c) => !cardCoincideCodigo(c, codigo)),
      ])
      setComparacionActiva(card)
    } catch {
      toast.error('Error al buscar el código')
    } finally {
      setLoadingCodigo(false)
    }
  }

  const filtrosCategoriaActivos = mismaCategoria || mismaSubcategoria

  // Al activar un filtro por categoría/subcategoría, traer el detalle de todos
  // los candidatos que falten para que el filtro aplique sobre la lista entera.
  useEffect(() => {
    if (
      modalOpen &&
      !comparacionActiva &&
      filtrosCategoriaActivos &&
      candidatos.length > 0 &&
      loadedCount < candidatos.length &&
      !loadingDetalle
    ) {
      cargarRestantes()
    }
  }, [
    modalOpen,
    comparacionActiva,
    filtrosCategoriaActivos,
    candidatos.length,
    loadedCount,
    loadingDetalle,
    cargarRestantes,
  ])

  const materialOriginalVista = useMemo(
    () => ({
      ...materialOriginal,
      repuesto: enriquecerConJerarquiaSugerida(
        materialOriginal.repuesto,
        catalogs,
        getJerarquiaFormulario(),
      ),
    }),
    [materialOriginal, catalogs, getJerarquiaFormulario],
  )

  // Categoría/subcategoría ya guardada (selects), no la jerarquía AX original.
  const categoriaOriginal = normalizarValorComparacion(
    materialOriginalVista.repuesto?.revision_categoria_nombre ??
      material?.revision_categoria_nombre ??
      material?.categoria_nombre,
  )
  const subcategoriaOriginal = normalizarValorComparacion(
    materialOriginalVista.repuesto?.revision_subcategoria_nombre ??
      material?.revision_subcategoria_nombre ??
      material?.subcategoria_nombre,
  )

  const cardsFiltradas = useMemo(() => {
    let res = cards
    if (mismaCategoria && categoriaOriginal) {
      res = res.filter(
        (c) =>
          c.busquedaManual ||
          normalizarValorComparacion(
            c.repuesto?.revision_categoria_nombre ?? c.repuesto?.categoria_nombre,
          ) === categoriaOriginal,
      )
    }
    if (mismaSubcategoria && subcategoriaOriginal) {
      res = res.filter(
        (c) =>
          c.busquedaManual ||
          normalizarValorComparacion(
            c.repuesto?.revision_subcategoria_nombre ??
              c.repuesto?.subcategoria_nombre,
          ) === subcategoriaOriginal,
      )
    }
    return res
  }, [
    cards,
    mismaCategoria,
    mismaSubcategoria,
    categoriaOriginal,
    subcategoriaOriginal,
  ])

  const hayMasCandidatos = loadedCount < candidatos.length
  const tituloCooldown =
    cooldownSeconds > 0
      ? `Espere ${cooldownSeconds}s tras subir la primera foto`
      : undefined

  const codigoOriginal =
    materialOriginal.repuesto?.repuesto_codigo_material_sap ||
    normalizarCodigo(codigoMaterial)

  const candidatoRepuestoId = comparacionActiva?.repuesto?.repuesto_id
  const yaEsReemplazante = candidatoRepuestoId
    ? reemplazantes.some((r) => r.repuesto_id === candidatoRepuestoId)
    : false
  const yaEsSustituto = candidatoRepuestoId
    ? sustitutos.some((s) => s.repuesto_id === candidatoRepuestoId)
    : false
  const esMismoMaterial =
    candidatoRepuestoId != null &&
    material?.repuesto_id != null &&
    candidatoRepuestoId === material.repuesto_id

  const baseAccion = !isReadOnly && !disabled && Boolean(candidatoRepuestoId)

  const puedeMatchReemplazante =
    baseAccion &&
    !esMismoMaterial &&
    !yaEsReemplazante &&
    Boolean(onMatchReemplazante)
  const puedeMatchSustituto =
    baseAccion &&
    !esMismoMaterial &&
    !yaEsSustituto &&
    Boolean(onMatchSustituto)
  const puedeQuitarReemplazante =
    baseAccion && yaEsReemplazante && Boolean(onUnmatchReemplazante)
  const puedeQuitarSustituto =
    baseAccion && yaEsSustituto && Boolean(onUnmatchSustituto)

  const dispararCelebracion = (tipoLabel) => {
    setCelebracion({
      imagenIzq: getPrimeraImagenUrl(
        materialOriginal.imagenes,
        imagenesMaterial,
      ),
      imagenDer: getPrimeraImagenUrl(comparacionActiva.imagenes),
      codigoIzq: codigoOriginal,
      codigoDer:
        comparacionActiva.repuesto?.repuesto_codigo_material_sap ||
        comparacionActiva.codigo,
      similitud: formatSimilitud(comparacionActiva.similitud),
      tipo: tipoLabel,
    })
    setTimeout(() => setCelebracion(null), 1900)
  }

  const ejecutarAccion = async (accion, fn, tipoCelebrar) => {
    setAccionEnCurso(accion)
    try {
      const ok = await fn(comparacionActiva.repuesto)
      if (ok && tipoCelebrar) dispararCelebracion(tipoCelebrar)
    } finally {
      setAccionEnCurso(null)
    }
  }

  const handleMatchReemplazante = () => {
    if (!puedeMatchReemplazante) return
    ejecutarAccion('match-reempl', onMatchReemplazante, 'Reemplazante')
  }

  const handleQuitarReemplazante = () => {
    if (!puedeQuitarReemplazante) return
    ejecutarAccion('unmatch-reempl', onUnmatchReemplazante)
  }

  const handleMatchSustituto = () => {
    if (!puedeMatchSustituto) return
    ejecutarAccion('match-sust', onMatchSustituto, 'Sustituto')
  }

  const handleQuitarSustituto = () => {
    if (!puedeQuitarSustituto) return
    ejecutarAccion('unmatch-sust', onUnmatchSustituto)
  }

  return (
    <>
      {mostrarBoton && (
        <div className="buscar-reemplazantes-section">
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleBuscar}
            loading={loadingBuscar}
            disabled={disabled}
            block
            title={tituloCooldown}
            className="buscar-reemplazantes-btn"
          >
            Buscar similares
          </Button>
          {cooldownSeconds > 0 && (
            <p className="buscar-reemplazantes-cooldown-hint" role="status">
              Procesando imagen… disponible en {cooldownSeconds}s
            </p>
          )}
        </div>
      )}

      <Modal
        title={
          comparacionActiva
            ? 'Comparación de materiales'
            : 'Candidatos reemplazantes'
        }
        open={modalOpen}
        onCancel={handleCerrarModal}
        footer={null}
        width={comparacionActiva ? 920 : 720}
        destroyOnClose
        className={`buscar-reemplazantes-modal${
          comparacionActiva ? ' buscar-reemplazantes-modal--vs' : ''
        }`}
        centered
        style={{ maxWidth: 'calc(100vw - 300px)' }}
        styles={{
          body: {
            padding: comparacionActiva ? 0 : 24,
            maxHeight: '88vh',
            overflow: comparacionActiva ? 'hidden' : 'auto',
          },
        }}
      >
        {celebracion && <MatchCelebration {...celebracion} />}
        {!comparacionActiva && (
          <div className="buscar-reemplazantes-modal-toolbar">
            <Input.Search
              className="buscar-reemplazantes-codigo-search"
              placeholder="Buscar por código SAP"
              allowClear
              enterButton
              value={codigoBusqueda}
              onChange={(e) => setCodigoBusqueda(e.target.value)}
              onSearch={handleBuscarPorCodigo}
              loading={loadingCodigo}
              disabled={loadingCodigo || (loadingDetalle && cards.length === 0)}
            />
            <div className="buscar-reemplazantes-modal-filters">
              <Checkbox
                className="buscar-reemplazantes-misma-marca"
                checked={mismaMarca}
                onChange={(e) => handleToggleMismaMarca(e.target.checked)}
                disabled={loadingDetalle}
              >
                Solo de la misma marca
              </Checkbox>
              <Checkbox
                className="buscar-reemplazantes-misma-marca"
                checked={mismaCategoria}
                onChange={(e) => setMismaCategoria(e.target.checked)}
                disabled={loadingDetalle || !categoriaOriginal}
              >
                Misma categoría
              </Checkbox>
              <Checkbox
                className="buscar-reemplazantes-misma-marca"
                checked={mismaSubcategoria}
                onChange={(e) => setMismaSubcategoria(e.target.checked)}
                disabled={loadingDetalle || !subcategoriaOriginal}
              >
                Misma subcategoría
              </Checkbox>
            </div>
          </div>
        )}
        {comparacionActiva ? (
          <TinderVsComparison
            materialOriginal={materialOriginalVista}
            comparacionActiva={comparacionActiva}
            imagenesMaterial={imagenesMaterial}
            codigoOriginal={codigoOriginal}
            yaEsReemplazante={yaEsReemplazante}
            yaEsSustituto={yaEsSustituto}
            esMismoMaterial={esMismoMaterial}
            puedeMatchReemplazante={puedeMatchReemplazante}
            puedeMatchSustituto={puedeMatchSustituto}
            puedeQuitarReemplazante={puedeQuitarReemplazante}
            puedeQuitarSustituto={puedeQuitarSustituto}
            accionEnCurso={accionEnCurso}
            onVolverCandidatos={() => setComparacionActiva(null)}
            onMatchReemplazante={handleMatchReemplazante}
            onQuitarReemplazante={handleQuitarReemplazante}
            onMatchSustituto={handleMatchSustituto}
            onQuitarSustituto={handleQuitarSustituto}
          />
        ) : loadingDetalle && cards.length === 0 ? (
          <div className="buscar-reemplazantes-modal-loading">
            <Spin tip="Cargando candidatos..." />
          </div>
        ) : cards.length === 0 ? (
          <Empty description="Sin candidatos. Busca un código SAP para comparar." />
        ) : (
          <>
            <p className="buscar-reemplazantes-modal-count">
              Mostrando {cardsFiltradas.length} de {candidatos.length}{' '}
              candidatos
              {filtrosCategoriaActivos ? ' (filtrados)' : ''}
            </p>
            {cardsFiltradas.length > 0 ? (
              <div className="buscar-reemplazantes-modal-cards">
                {cardsFiltradas.map((card, index) => (
                  <CandidatoReemplazanteCard
                    key={card.key}
                    card={card}
                    index={index}
                    reemplazantes={reemplazantes}
                    sustitutos={sustitutos}
                    onClick={handleCardClick}
                  />
                ))}
                {!filtrosCategoriaActivos && hayMasCandidatos && (
                  <div className="buscar-reemplazantes-modal-footer">
                    <Button
                      type="default"
                      onClick={handleVerMas}
                      loading={loadingDetalle}
                      block
                    >
                      Ver más
                    </Button>
                  </div>
                )}
              </div>
            ) : loadingDetalle ? (
              <div className="buscar-reemplazantes-modal-loading">
                <Spin tip="Cargando candidatos..." />
              </div>
            ) : (
              <Empty description="Ningún candidato coincide con los filtros" />
            )}
            {filtrosCategoriaActivos
              ? loadingDetalle &&
                cardsFiltradas.length > 0 && (
                  <div className="buscar-reemplazantes-modal-loading">
                    <Spin tip="Cargando candidatos para filtrar..." />
                  </div>
                )
              : null}
          </>
        )}
      </Modal>
    </>
  )
}

export default BuscarReemplazantesSection





